import { useEffect, useMemo, useRef, useState, type JSX } from "react";
import { EmptyState, ErrorState, Skeleton, cn } from "@mailflare/ui";
import type {
  BulkAction,
  LabelId,
  MailboxWithCounts,
  MessageId,
  MessageSummary,
} from "@mailflare/shared";

import { useKeyboardShortcuts } from "@/hooks/use-keyboard-shortcuts";
import { useMailboxEvents } from "@/hooks/use-mailbox-events";
import { useLabels } from "@/features/labels/hooks";
import { useComposeStore } from "@/stores/compose-store";
import { useAnnouncerStore } from "@/stores/announcer-store";
import { deriveReplyPrefill } from "@/stores/compose-store";

import { MessageList } from "./message-list";
import { ReadingPane } from "./reading-pane";
import { useBulkMessageAction, useMessage, useUpdateMessage } from "./hooks";

export interface MailWorkspaceProps {
  readonly mailbox: MailboxWithCounts;
  readonly messages: readonly MessageSummary[];
  readonly listLabel: string;
  readonly emptyTitle: string;
  readonly emptyDescription?: string;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly onRetry: () => void;
  readonly hasNextPage: boolean;
  readonly isFetchingNextPage: boolean;
  readonly onLoadMore: () => void;
  readonly selectedId: MessageId | null;
  readonly onSelect: (id: MessageId | null) => void;
}

function ListSkeleton(): JSX.Element {
  return (
    <div className="space-y-3 p-3" aria-busy="true" aria-label="Loading messages">
      {Array.from({ length: 8 }, (_, index) => (
        <Skeleton key={index} className="h-16 w-full" />
      ))}
    </div>
  );
}

/** Message list + reading pane with selection, keyboard navigation, and revalidation. */
export function MailWorkspace({
  mailbox,
  messages,
  listLabel,
  emptyTitle,
  emptyDescription,
  isLoading,
  isError,
  onRetry,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  selectedId,
  onSelect,
}: MailWorkspaceProps): JSX.Element {
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const detailQuery = useMessage(selectedId);
  const labelsQuery = useLabels();
  const updateMessage = useUpdateMessage(mailbox.id);
  const bulkAction = useBulkMessageAction(mailbox.id);
  const openCompose = useComposeStore((state) => state.openCompose);
  const announce = useAnnouncerStore((state) => state.announce);

  useMailboxEvents(mailbox.id, { onAnnounce: announce });

  const selectedIndex = useMemo(
    () => (selectedId === null ? -1 : messages.findIndex((message) => message.id === selectedId)),
    [messages, selectedId],
  );

  useEffect(() => {
    setFocusedIndex(selectedIndex);
  }, [selectedIndex]);

  const detail = detailQuery.data;
  const markedRead = useRef(new Set<string>());
  useEffect(() => {
    if (detail !== undefined && !detail.isRead && !markedRead.current.has(detail.id)) {
      markedRead.current.add(detail.id);
      updateMessage.mutate({ messageId: detail.id, body: { isRead: true } });
    }
  }, [detail, updateMessage.mutate]);

  const move = (delta: number): void => {
    if (messages.length === 0) return;
    const base = selectedIndex >= 0 ? selectedIndex : -1;
    const next = Math.min(Math.max(base + delta, 0), messages.length - 1);
    const message = messages[next];
    if (message !== undefined) onSelect(message.id);
  };

  const handleSelect = (message: MessageSummary): void => {
    onSelect(message.id);
  };

  const handleToggleFlag = (message: MessageSummary): void => {
    updateMessage.mutate({ messageId: message.id, body: { isFlagged: !message.isFlagged } });
  };

  const runBulk = (action: BulkAction): void => {
    if (selectedId === null) return;
    bulkAction.mutate({ ids: [selectedId], action });
  };

  const openReply = (mode: "reply" | "reply_all" | "forward"): void => {
    if (detail === undefined) return;
    openCompose({
      mode,
      sourceMessageId: detail.id,
      prefill: deriveReplyPrefill(detail, mode, mailbox.address),
    });
  };

  useKeyboardShortcuts({
    onNext: () => move(1),
    onPrevious: () => move(-1),
    onArchive: () => {
      if (selectedId !== null) bulkAction.mutate({ ids: [selectedId], action: "archive" });
    },
    onDelete: () => {
      if (selectedId !== null) bulkAction.mutate({ ids: [selectedId], action: "move_trash" });
    },
    onReply: () => openReply("reply"),
    onFlag: () => {
      const message = messages.find((item) => item.id === selectedId);
      if (message !== undefined) handleToggleFlag(message);
    },
    onEscape: () => onSelect(null),
  });

  return (
    <div className="flex min-h-0 flex-1">
      <section
        aria-label={listLabel}
        className={cn(
          "border-border flex min-h-0 w-full flex-col border-r lg:w-[380px]",
          selectedId !== null && "hidden lg:flex",
        )}
      >
        <div className="border-border flex h-14 items-center justify-between px-4 py-2">
          <h1 className="text-base font-medium">{listLabel}</h1>
          <span className="text-muted-foreground text-xs" aria-live="polite">
            {messages.length} message{messages.length === 1 ? "" : "s"}
          </span>
        </div>
        {isError ? (
          <ErrorState
            message="Messages could not be loaded."
            onRetry={onRetry}
            className="m-auto"
          />
        ) : isLoading ? (
          <ListSkeleton />
        ) : messages.length === 0 ? (
          <EmptyState
            title={emptyTitle}
            {...(emptyDescription !== undefined ? { description: emptyDescription } : {})}
            className="m-auto"
          />
        ) : (
          <MessageList
            messages={messages}
            selectedId={selectedId}
            focusedIndex={focusedIndex}
            ariaLabel={listLabel}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onSelect={handleSelect}
            onToggleFlag={handleToggleFlag}
            onLoadMore={onLoadMore}
          />
        )}
      </section>

      <section
        className={cn(
          "flex min-h-0 min-w-0 flex-1 flex-col",
          selectedId === null && "hidden lg:flex",
        )}
      >
        <ReadingPane
          messageId={selectedId}
          detail={detail}
          isLoading={detailQuery.isLoading}
          isError={detailQuery.isError}
          labels={labelsQuery.data ?? []}
          onRetry={() => void detailQuery.refetch()}
          onClose={() => onSelect(null)}
          onReply={openReply}
          onArchive={() => runBulk("archive")}
          onTrash={() => runBulk("move_trash")}
          onToggleFlag={() => {
            if (detail !== undefined) handleToggleFlag(detail);
          }}
          onToggleLabel={(labelId: LabelId, present: boolean) => {
            if (selectedId === null) return;
            bulkAction.mutate({
              ids: [selectedId],
              action: present ? "add_label" : "remove_label",
              labelId,
            });
          }}
        />
      </section>
    </div>
  );
}
